/**
 * CMV — Canadian Market Value
 * Shared logic for pricing zones, formatting, and validation.
 */

export function formatCanadianCurrency(n) {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(n);
}

/**
 * Classify a price into one of five CMV zones.
 * Returns: 'too-low' | 'good-price' | 'best-value' | 'above-market' | 'too-high'
 */
export function getCMVStatus(price, fairRangeLow, fairRangeHigh, marketLow, marketHigh) {
  const belowGap = fairRangeLow - marketLow;
  const aboveGap = marketHigh - fairRangeHigh;
  if (price < fairRangeLow - belowGap * 0.45) return 'too-low';
  if (price < fairRangeLow) return 'good-price';
  if (price <= fairRangeHigh) return 'best-value';
  if (price <= fairRangeHigh + aboveGap * 0.45) return 'above-market';
  return 'too-high';
}

/** Price as 0–1 position along the market range. */
export function calculateCMVPosition(price, marketLow, marketHigh) {
  return Math.max(0, Math.min(1, (price - marketLow) / (marketHigh - marketLow)));
}

/** Hill Y coordinate from price (Gaussian bell curve). */
export function calculateHillCoordinates(price, hillCenter, hillSigma, hillBottom, hillHeight) {
  const z = (price - hillCenter) / hillSigma;
  return hillBottom - hillHeight * Math.exp(-0.5 * z * z);
}

/** Clamp popup X so it stays within padding bounds. */
export function clampPopupPosition(rawX, popupWidth, padLeft, padRight) {
  return Math.max(padLeft, Math.min(rawX, padRight - popupWidth));
}

/**
 * Validate a CMV API response object.
 * Ensures the five price points are present and in ascending order.
 */
export function validateCMVResponse(data) {
  if (!data || typeof data !== 'object') return false;
  const { marketLow, fairRangeLow, estimatedValue, fairRangeHigh, marketHigh } = data;
  const vals = [marketLow, fairRangeLow, estimatedValue, fairRangeHigh, marketHigh];
  if (vals.some(v => typeof v !== 'number' || isNaN(v) || !isFinite(v))) return false;
  return (
    marketLow <= fairRangeLow &&
    fairRangeLow <= estimatedValue &&
    estimatedValue <= fairRangeHigh &&
    fairRangeHigh <= marketHigh
  );
}

/**
 * Confidence in a market-value figure, derived from what the estimate was
 * actually built on — never asserted or guessed.
 *
 *   high     VIN-specific valuation (the vehicle itself was decoded and priced)
 *   moderate year/make/model comparables backed by an observable sample of
 *              live listings or recent sales
 *   low      year/make/model comparables with no sample size behind them
 *
 * @param {object} data market data as reported by MarketValuePricingTool
 * @returns {'high'|'moderate'|'low'}
 */
export function getMarketValueConfidence(data) {
  if (!data) return 'low';
  if (data.isVinSpecific) return 'high';
  const mi = data.marketIntelligence || {};
  const hasSample =
    (typeof mi.active_listings === 'number' && mi.active_listings > 0) ||
    (typeof mi.sold_last_90_days === 'number' && mi.sold_last_90_days > 0);
  return hasSample ? 'moderate' : 'low';
}

/**
 * The assumptions behind a market-value figure, stated plainly so a seller can
 * judge it. Lists what was actually used and — just as important — what was
 * not, so an omission is never mistaken for a factor that was considered.
 *
 * @param {object} data    market data as reported by MarketValuePricingTool
 * @param {object} inputs  { vin, mileage, province, condition } as entered
 * @returns {{ used: string[], notFactored: string[] }}
 */
export function getMarketValueAssumptions(data, inputs = {}) {
  const { vin, mileage, province, condition } = inputs;
  const used = [];
  const notFactored = [];

  if (data?.isVinSpecific) {
    used.push('This vehicle’s VIN, decoded to its exact trim and factory equipment');
  } else if (vin) {
    used.push('Year, make and model');
  } else {
    used.push('Year, make and model (no VIN provided)');
  }

  if (mileage) {
    used.push(`Odometer reading of ${Number(mileage).toLocaleString('en-CA')} km`);
  } else {
    notFactored.push('Odometer reading — not entered');
  }

  if (!data?.isVinSpecific) {
    if (province) {
      used.push(`Comparable listings in ${province}`);
    } else {
      notFactored.push('Your province — comparables are Canada-wide');
    }
    if (condition) {
      used.push(`Stated condition: ${condition}`);
    } else {
      notFactored.push('Vehicle condition — not entered');
    }
  } else {
    // VinAudit prices the vehicle, not its condition or damage history: those
    // are not inputs to that valuation, so saying otherwise would be false.
    notFactored.push('Current condition, wear, or damage');
    notFactored.push('Accident or claims history');
  }

  notFactored.push('Aftermarket modifications or extras');

  return { used, notFactored };
}
